/**
 * The handful of primitives every game screen shares. Deliberately tiny — SPEC
 * §4.1 rules out a component library because the visuals are highly custom.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

/* -------------------------------------------------------------------------- */
/* Screen shell                                                                */
/* -------------------------------------------------------------------------- */

export interface ScreenProps {
  /**
   * Fixed chrome pinned under the status row — today only the countdown strip
   * (DESIGN.md §3.5). It is a constant height whether it is running or locked,
   * because the deck's whole point about the timer is that the screen beneath
   * it does not move, resize or change on the way to zero.
   *
   * It sits *below* the status row rather than above it because the status row
   * is the screen's identity — "PREDICT OMAR · Review" is true for the whole
   * phase, and the strip is a thing that happens inside it.
   */
  strip?: ReactNode;
  /** Status row, left: small letterspaced uppercase — "YOUR TURN", "REVEAL · 23". */
  eyebrow?: ReactNode;
  /** Status row, right: small meta label — "Draft saved", "8 used", "LIVE". */
  meta?: ReactNode;
  /** Sentence case, with a full stop: "Pick a number." */
  title?: ReactNode;
  /** One short line under the heading. Most screens pass none. */
  sub?: ReactNode;
  children: ReactNode;
  /** Sticky bottom bar — primary actions live here, thumb-reachable. */
  footer?: ReactNode;
  /** Set when the body owns its own scrolling (the ranking screen). */
  bodyClassName?: string;
  /** Alias for `bodyClassName`, for screens that treat the body as the screen. */
  className?: string;
  testId?: string;
}

/**
 * The small letterspaced caps label (DESIGN.md §1) — the status row's left slot,
 * a section kicker, a card's label row. Green is the default because that is
 * what the deck uses for a kicker; the status row overrides to `ink` and a
 * history row to `ink-soft`.
 *
 * It exists as a primitive because eight screens were each writing the same six
 * utility classes, and a letterspacing that drifts by a hundredth of an em
 * between two adjacent screens is exactly the kind of seam a reader notices
 * without being able to name.
 */
export function Eyebrow({
  as: Tag = 'p',
  tone = 'green',
  className = '',
  children,
}: {
  /**
   * `h2`/`h3` where the kicker really is the section's heading — `h3` for a
   * section nested under one that already has an `h2`, so the outline does not
   * skip a level. `span` where the label sits inside a button (flow content
   * inside a `<button>` is invalid).
   */
  as?: 'p' | 'span' | 'h2' | 'h3';
  tone?: 'green' | 'ink' | 'ink-soft';
  className?: string;
  children: ReactNode;
}) {
  const tones = {
    green: 'text-green',
    ink: 'text-ink',
    'ink-soft': 'text-ink-soft',
  } as const;
  return (
    <Tag
      className={`text-[0.6875rem] font-semibold tracking-[0.12em] uppercase ${tones[tone]} ${className}`}
    >
      {children}
    </Tag>
  );
}

/**
 * A game screen's internal layout: optional strip, status row, heading,
 * scrolling body, sticky footer.
 *
 * The portrait/desktop column itself (`phone-column`, SPEC §5.6) is provided by
 * the shell — we do not invent a second one, and we do not repeat it here, or
 * the column would be nested twice.
 */
export function Screen({
  strip,
  eyebrow,
  meta,
  title,
  sub,
  children,
  footer,
  bodyClassName = '',
  className = '',
  testId,
}: ScreenProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid={testId}>
      {(eyebrow || meta) && (
        <div className="flex shrink-0 items-baseline justify-between gap-3 px-5 pt-3">
          <Eyebrow tone="ink">{eyebrow}</Eyebrow>
          <p className="shrink-0 text-[0.6875rem] whitespace-nowrap text-ink-soft">{meta}</p>
        </div>
      )}
      {strip && <div className="sticky top-0 z-20 mt-3 h-11 shrink-0">{strip}</div>}
      {(title || sub) && (
        <header className="shrink-0 px-5 pt-3 pb-3">
          {title && (
            <h1 className="text-[1.75rem] leading-[1.15] font-semibold tracking-tight text-ink">
              {title}
            </h1>
          )}
          {sub && <p className="mt-1.5 text-[0.8125rem] leading-snug text-ink-soft">{sub}</p>}
        </header>
      )}
      <main className={`flex min-h-0 flex-1 flex-col px-5 pb-2 ${bodyClassName} ${className}`}>
        {children}
      </main>
      {footer && <footer className="shrink-0 px-5 pt-2 pb-4">{footer}</footer>}
    </div>
  );
}

/**
 * The countdown chrome (DESIGN.md §3.5). Amber while the answers can still be
 * edited, ink once they are gone; identical geometry either way, so the strip
 * changing state never reflows the screen it sits on.
 */
export function CountdownStrip({
  tone,
  children,
}: {
  tone: 'running' | 'locked';
  children: ReactNode;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex h-11 items-center justify-center text-[0.75rem] font-semibold tracking-[0.12em] text-cream uppercase ${
        tone === 'running' ? 'bg-amber' : 'bg-ink'
      }`}
    >
      {children}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Button                                                                      */
/* -------------------------------------------------------------------------- */

type Variant = 'primary' | 'secondary' | 'equal' | 'equal-alt' | 'quiet' | 'ghost' | 'danger';

const DISABLED = 'disabled:border-transparent disabled:bg-disabled disabled:text-ink-soft/45';

const VARIANTS: Record<Variant, string> = {
  primary: `border-transparent bg-green text-cream ${DISABLED}`,
  secondary: `border-line bg-cream text-ink ${DISABLED}`,
  /**
   * The co-equal pair, which in this app means the camera and the camera roll:
   * SPEC §6 says upload is an *equal* alternative, and treating it as a
   * secondary action would quietly make it a fallback.
   *
   * **Use them as a pair, never one alone, and never `primary` alongside one of
   * them.** An earlier attempt paired `equal` with `primary` — same size, same
   * filled weight, different hue — and a reviewer read it straight off the
   * screen as primary-and-secondary, because that was the app's colour for "the
   * thing to press". Equal size is not equal weight. What survives that finding
   * is the geometry rule: the two halves are the same height, radius, size and
   * weight, and sit in the same stack, so neither reads as the recovery path.
   * The deck's fills differ (green solid over cream bordered) and that is fine
   * precisely because the pair is named as a pair rather than borrowed from the
   * primary/secondary ladder every other screen uses.
   */
  equal: `border-transparent bg-green text-cream ${DISABLED}`,
  'equal-alt': `border-line bg-cream text-ink ${DISABLED}`,
  /** The quiet green text action: "← Edit first pick · Omar". */
  quiet: 'border-transparent bg-transparent text-green disabled:text-ink-soft/45',
  ghost: 'border-transparent bg-transparent text-ink-soft disabled:text-ink-soft/45',
  danger: `border-transparent bg-brick text-cream ${DISABLED}`,
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  full?: boolean;
}

export function Button({
  variant = 'primary',
  full = true,
  className = '',
  type = 'button',
  ...rest
}: ButtonProps) {
  const quiet = variant === 'quiet' || variant === 'ghost';
  return (
    <button
      type={type}
      className={[
        'inline-flex items-center justify-center rounded-btn border px-5',
        quiet ? 'min-h-11 text-[0.8125rem]' : 'min-h-13 text-base',
        'font-semibold transition active:scale-[0.99] disabled:cursor-not-allowed disabled:active:scale-100',
        VARIANTS[variant],
        full ? 'w-full' : '',
        className,
      ].join(' ')}
      {...rest}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Waiting state                                                               */
/* -------------------------------------------------------------------------- */

/**
 * The "you are not the one doing anything right now" screen. Used a lot: the
 * game is deliberately asymmetric, and a dead screen is the port's central
 * problem (SPEC §1), so these always say what is being waited on.
 */
export function WaitingFor({
  headline,
  detail,
  children,
}: {
  headline: ReactNode;
  detail?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
      <p className="text-xl leading-tight font-semibold text-ink">{headline}</p>
      {detail && <p className="max-w-[16rem] text-[0.8125rem] text-ink-soft">{detail}</p>}
      {children}
    </div>
  );
}

/*
  `PredictionCounter` — "7 of 11 predictions in", with a progress bar — used to
  live here, on the turn player's ranking and lock screens. It is deleted, not
  moved: SPEC §2.3 phase 2 now says the turn player is told nothing about the
  predictions, and predictors get the room board's faces instead of a tally
  (DESIGN §3.4). There is nobody left to show a count to.
*/
